import React from 'react';
import { TimesIcon } from '@patternfly/react-icons';
import { colors, radii, shadows } from '@app/components/designTokens';
import { useWindowSize, isMobile } from '@app/utils/useWindowSize';

export type AppModalWidth = 'sm' | 'md' | 'lg' | 'xl';

const WIDTH_MAP: Record<AppModalWidth, number> = {
    sm: 400,
    md: 480,
    lg: 600,
    xl: 760
};

interface AppModalProps {
    isOpen: boolean;
    onClose: () => void;
    width?: AppModalWidth;
    /** Set false to keep modal open when clicking the backdrop (useful for forms with destructive data). */
    closeOnBackdropClick?: boolean;
    /** Hide the close X (e.g. when the modal owns its own close path). */
    hideCloseButton?: boolean;
    /** Extra style merged into the modal card. */
    modalStyle?: React.CSSProperties;
    children: React.ReactNode;
}

const AppModal: React.FC<AppModalProps> = ({
    isOpen,
    onClose,
    width = 'md',
    closeOnBackdropClick = true,
    hideCloseButton = false,
    modalStyle,
    children
}) => {
    const { width: vw } = useWindowSize();
    const mobile = isMobile(vw);

    // Track where the mouse press started so a text-selection drag that
    // overshoots onto the backdrop doesn't dismiss the modal. We only close
    // when both the press and the release land on the overlay itself.
    const mouseDownTarget = React.useRef<EventTarget | null>(null);

    if (!isOpen) return null;

    const overlay: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: mobile ? '0' : '24px'
    };

    const modal: React.CSSProperties = {
        background: '#ffffff',
        borderRadius: mobile ? 0 : radii.card,
        width: '100%',
        maxWidth: mobile ? 'none' : WIDTH_MAP[width],
        maxHeight: mobile ? '100%' : '92vh',
        height: mobile ? '100%' : 'auto',
        overflow: 'auto',
        boxShadow: mobile ? 'none' : shadows.modal,
        position: 'relative',
        ...modalStyle
    };

    const closeButton: React.CSSProperties = {
        position: 'absolute',
        top: 16,
        right: 16,
        background: colors.bgMuted,
        border: 'none',
        borderRadius: '50%',
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: colors.subtle,
        transition: 'background 0.15s',
        zIndex: 2
    };

    return (
        <div
            style={overlay}
            onMouseDown={(e) => {
                mouseDownTarget.current = e.target;
            }}
            onMouseUp={(e) => {
                const pressedOnOverlay = mouseDownTarget.current === e.currentTarget;
                const releasedOnOverlay = e.target === e.currentTarget;
                mouseDownTarget.current = null;
                if (closeOnBackdropClick && pressedOnOverlay && releasedOnOverlay) {
                    onClose();
                }
            }}
        >
            <div style={modal} onClick={(e) => e.stopPropagation()}>
                {!hideCloseButton && (
                    <button
                        style={closeButton}
                        onClick={onClose}
                        aria-label="Close"
                        type="button"
                    >
                        <TimesIcon />
                    </button>
                )}
                {children}
            </div>
        </div>
    );
};

export { AppModal };
